import React, { useState, useEffect, useCallback, useRef } from 'react';
import { AppLayout } from '../components/AppLayout';
import { Button } from '../components/ui/button';
import {
  FileSignature, ChevronLeft, ChevronRight, Printer, CheckCircle2, Rocket,
  KeyRound, Workflow, ListChecks, AlertTriangle, Camera, PenTool, Upload,
  Bell, Lightbulb, HelpCircle, LifeBuoy, ClipboardList, Maximize, Minimize,
} from 'lucide-react';

const SLIDES = [
  {
    key: 'cover', accent: 'from-indigo-600 to-purple-600', icon: FileSignature,
    title: 'Le PV de Livraison',
    subtitle: 'Guide de présentation & d\'utilisation — Prestataires',
    body: (
      <div className="space-y-4 text-lg text-white/90">
        <p>Le procès-verbal qui <strong>acte la fin de l'installation</strong> d'un corner et <strong>valide sa conformité</strong>, signé en ligne avec le magasin.</p>
        <p className="text-white/70">WASABI CAPEX — Onboarding prestataires</p>
      </div>
    ),
  },
  {
    key: 'what', accent: 'from-blue-500 to-blue-600', icon: FileSignature,
    title: "Qu'est-ce que c'est ?",
    bullets: [
      "Le document officiel de réception des travaux d'installation d'un corner.",
      "Auparavant sur papier — désormais 100 % numérique (ordinateur, tablette, smartphone).",
      "Rempli, photographié et signé directement en ligne, en présence du client.",
    ],
    highlight: "En une phrase : c'est votre bon de réception digital, signé par le magasin, qui prouve que le chantier est livré et conforme.",
  },
  {
    key: 'why', accent: 'from-emerald-500 to-emerald-600', icon: Rocket,
    title: 'Pourquoi c\'est important pour vous',
    cards: [
      { t: 'Preuve de livraison', d: 'Document horodaté et signé, opposable en cas de litige.' },
      { t: 'Plus rapide', d: 'Saisie et signature sur place en quelques minutes, fini le papier perdu.' },
      { t: 'Traçabilité photo', d: 'Photos de fin de chantier et des réserves attachées au PV.' },
      { t: 'Sécurise le paiement', d: 'Un chantier livré et signé = un chantier validé.' },
      { t: 'Zéro oubli', d: 'Rappels automatiques des PV non signés.' },
    ],
  },
  {
    key: 'access', accent: 'from-slate-600 to-slate-700', icon: KeyRound,
    title: 'Accès à la plateforme',
    bullets: [
      "Connexion avec votre identifiant prestataire (email + mot de passe fournis par WASABI).",
      "Vous ne voyez que vos propres chantiers et PV (données cloisonnées).",
      "Le PV est accessible depuis le chantier / la commande concerné, ou la liste des PV.",
    ],
    highlight: "🔐 Accès oublié ? Contactez votre référent WASABI.",
  },
  {
    key: 'lifecycle', accent: 'from-amber-500 to-orange-600', icon: Workflow,
    title: 'Le cycle de vie d\'un PV',
    steps: [
      { n: '1', t: 'Brouillon', d: 'Création + remplissage (infos, checklist, photos).' },
      { n: '2', t: 'Prêt à signer', d: 'Tout est rempli, on peut signer avec le magasin.' },
      { n: '3', t: 'Signé ✅', d: 'Client + poseur ont signé. PV verrouillé et envoyé.' },
    ],
    highlight: "Le PV est créé automatiquement depuis la commande / le chantier — infos et photos déjà pré-remplies.",
  },
  {
    key: 'anatomy', accent: 'from-cyan-500 to-blue-600', icon: ClipboardList,
    title: 'Les informations à vérifier',
    bullets: [
      "Enseigne, nom du magasin, adresse complète.",
      "Date d'installation, prestataire / poseur, responsable WASABI.",
      "Contact magasin + EMAIL du contact → destinataire du PV signé (à vérifier !).",
      "Référence commande, type de chantier (Ouverture / Remodeling / SAV).",
    ],
  },
  {
    key: 'checklist', accent: 'from-emerald-500 to-teal-600', icon: ListChecks,
    title: 'La checklist de conformité',
    columns: [
      'Corner livré sur site',
      'Corner installé conformément au plan',
      'Mobilier inox posé',
      'Menuiserie / habillage posé',
      'Vitrine(s) installée(s)',
      'PLV / enseigne / signalétique',
      'Raccordements techniques réalisés',
      'État général conforme',
      'Aucune réserve constatée',
      'Réserves constatées (si besoin)',
      'Photos de fin de chantier ajoutées',
    ],
    highlight: "Cochez « Aucune réserve » si tout est conforme, sinon « Réserves constatées ».",
  },
  {
    key: 'reserves', accent: 'from-red-500 to-rose-600', icon: AlertTriangle,
    title: 'Gérer les réserves',
    bullets: [
      "Une réserve = un point non conforme ou inachevé constaté à la livraison.",
      "Chaque réserve : description + photo + statut (Mineure / Bloquante / À corriger / Corrigée).",
      "Le magasin PEUT signer un PV avec réserves : elles restent tracées jusqu'à correction.",
    ],
    highlight: "Bonne pratique : décrivez précisément chaque réserve ET joignez une photo.",
  },
  {
    key: 'photos', accent: 'from-fuchsia-500 to-purple-600', icon: Camera,
    title: 'Les photos',
    bullets: [
      "Photos de fin de chantier : importées automatiquement depuis le suivi de chantier (ajoutez-en si besoin).",
      "Photos de réclamations / réserves : à joindre pour documenter tout défaut.",
    ],
    highlight: "Une photo vaut mille mots : elle protège tout le monde et accélère les reprises.",
  },
  {
    key: 'sign', accent: 'from-indigo-500 to-blue-700', icon: PenTool,
    title: 'La signature (le moment clé)',
    steps: [
      { n: '1', t: 'Côté client', d: 'Nom + fonction, puis signature à l\'écran (+ tampon possible).' },
      { n: '2', t: 'Côté prestataire', d: 'Nom du poseur + signature.' },
      { n: '3', t: 'Validation', d: 'PV verrouillé, envoyé par email, PDF généré.' },
    ],
    highlight: "⚠️ Vérifiez l'email du contact magasin AVANT de signer : c'est le destinataire du PV final.",
  },
  {
    key: 'scan', accent: 'from-slate-500 to-slate-700', icon: Upload,
    title: 'Alternative : PV papier scanné',
    bullets: [
      "Si la signature numérique est impossible sur site (ex. pas de connexion) :",
      "1. Faire signer le PV sur papier.",
      "2. Le scanner ou le photographier.",
      "3. Le téléverser dans le PV (« PV signé scanné »).",
    ],
    highlight: "Le document reste centralisé et rattaché au chantier.",
  },
  {
    key: 'reminders', accent: 'from-amber-500 to-red-500', icon: Bell,
    title: 'Rappels automatiques',
    bullets: [
      "La plateforme surveille les PV non signés.",
      "À partir de J+7 après l'installation, un récapitulatif hebdomadaire est envoyé à l'équipe WASABI.",
      "Objectif : ne laisser aucun chantier sans PV signé.",
    ],
    highlight: "👉 Installez, vérifiez, faites signer le jour même quand c'est possible.",
  },
  {
    key: 'bestpractices', accent: 'from-emerald-600 to-green-700', icon: Lightbulb,
    title: 'Bonnes pratiques (mémo terrain)',
    bullets: [
      "Avant de partir : vérifiez que le PV existe pour la commande.",
      "Renseignez le bon email de contact magasin.",
      "Passez la checklist point par point devant le client.",
      "Photographiez la fin de chantier ET chaque réserve.",
      "Faites signer sur place (client + poseur).",
      "Relisez avant de valider : la signature verrouille le document.",
    ],
  },
  {
    key: 'faq', accent: 'from-blue-600 to-indigo-700', icon: HelpCircle,
    title: 'FAQ',
    qa: [
      { q: 'Modifier un PV après signature ?', a: 'Non, il est verrouillé. Contactez WASABI en cas d\'erreur.' },
      { q: 'Signer avec des réserves ?', a: 'Oui, elles sont enregistrées et suivies.' },
      { q: 'Je ne vois pas mon chantier ?', a: 'Vous ne voyez que vos dossiers. Contactez votre référent WASABI.' },
      { q: 'Où retrouver un PV signé ?', a: 'Dans la liste des PV (PDF téléchargeable + copie envoyée au magasin).' },
      { q: 'Pas de connexion sur site ?', a: 'Utilisez l\'option « PV signé scanné ».' },
    ],
  },
  {
    key: 'support', accent: 'from-indigo-600 to-purple-700', icon: LifeBuoy,
    title: 'Support',
    body: (
      <div className="space-y-4 text-lg text-white/90">
        <p>Une question sur le PV de Livraison ou un problème d'accès ?</p>
        <ul className="space-y-2">
          <li className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5" /> Votre référent WASABI habituel</li>
          <li className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5" /> L'assistance interne WASABI CAPEX</li>
        </ul>
        <p className="text-white/70 pt-4">Merci — et bonnes installations ! 🚀</p>
      </div>
    ),
  },
];

const SlideBody = ({ slide }) => {
  if (slide.body) return slide.body;
  return (
    <div className="space-y-5">
      {slide.subtitle && <p className="text-xl text-white/80">{slide.subtitle}</p>}

      {slide.bullets && (
        <ul className="space-y-3">
          {slide.bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-3 text-lg text-white/90">
              <CheckCircle2 className="w-5 h-5 mt-1 shrink-0 text-white/80" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      )}

      {slide.cards && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {slide.cards.map((c, i) => (
            <div key={i} className="rounded-xl bg-white/10 backdrop-blur p-4 border border-white/15">
              <p className="font-bold text-white">{c.t}</p>
              <p className="text-sm text-white/80 mt-1">{c.d}</p>
            </div>
          ))}
        </div>
      )}

      {slide.steps && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {slide.steps.map((s, i) => (
            <div key={i} className="rounded-xl bg-white/10 backdrop-blur p-4 border border-white/15">
              <div className="w-9 h-9 rounded-full bg-white/90 text-slate-900 font-bold flex items-center justify-center">{s.n}</div>
              <p className="font-bold text-white mt-3">{s.t}</p>
              <p className="text-sm text-white/80 mt-1">{s.d}</p>
            </div>
          ))}
        </div>
      )}

      {slide.columns && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
          {slide.columns.map((c, i) => (
            <div key={i} className="flex items-center gap-2 text-white/90">
              <span className="w-6 h-6 rounded-md bg-white/15 text-xs flex items-center justify-center font-semibold">{i + 1}</span>
              <span>{c}</span>
            </div>
          ))}
        </div>
      )}

      {slide.qa && (
        <div className="space-y-3">
          {slide.qa.map((x, i) => (
            <div key={i} className="rounded-lg bg-white/10 backdrop-blur p-3 border border-white/15">
              <p className="font-semibold text-white">{x.q}</p>
              <p className="text-sm text-white/80 mt-0.5">{x.a}</p>
            </div>
          ))}
        </div>
      )}

      {slide.highlight && (
        <div className="rounded-xl bg-white/95 text-slate-800 px-5 py-4 font-medium shadow-lg">
          {slide.highlight}
        </div>
      )}
    </div>
  );
};

export default function PVLivraisonGuidePage() {
  const [i, setI] = useState(0);
  const [isFs, setIsFs] = useState(false);
  const deckRef = useRef(null);
  const total = SLIDES.length;
  const next = useCallback(() => setI((v) => Math.min(v + 1, total - 1)), [total]);
  const prev = useCallback(() => setI((v) => Math.max(v - 1, 0)), []);

  const toggleFullscreen = useCallback(() => {
    const el = deckRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.().catch(() => {});
    } else {
      document.exitFullscreen?.();
    }
  }, []);

  useEffect(() => {
    const onFs = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, prev]);

  const slide = SLIDES[i];
  const Icon = slide.icon;

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto p-4 lg:p-6 space-y-4" data-testid="pv-guide-page">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <FileSignature className="w-6 h-6 text-indigo-600" /> Présentation — PV de Livraison
            </h1>
            <p className="text-sm text-slate-500">Support à présenter aux prestataires ({total} slides)</p>
          </div>
          <div className="flex items-center gap-2 print:hidden">
            <Button variant="outline" onClick={toggleFullscreen} className="gap-2" data-testid="pv-guide-fullscreen">
              {isFs ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
              {isFs ? 'Quitter' : 'Plein écran'}
            </Button>
            <Button variant="outline" onClick={() => window.print()} className="gap-2" data-testid="pv-guide-print">
              <Printer className="w-4 h-4" /> Imprimer / PDF
            </Button>
          </div>
        </div>

        {/* Deck (fullscreen target) */}
        <div ref={deckRef} className="bg-white dark:bg-slate-950 rounded-2xl space-y-4 data-[fs=true]:p-6 data-[fs=true]:flex data-[fs=true]:flex-col data-[fs=true]:justify-center data-[fs=true]:h-full" data-fs={isFs}>
        {/* Progress */}
        <div className="h-1.5 w-full bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden print:hidden">
          <div className="h-full bg-indigo-600 transition-all duration-300" style={{ width: `${((i + 1) / total) * 100}%` }} />
        </div>

        {/* Slide */}
        <div
          className={`relative rounded-3xl bg-gradient-to-br ${slide.accent} p-8 sm:p-12 min-h-[460px] shadow-xl overflow-hidden`}
          data-testid={`pv-slide-${slide.key}`}
        >
          <div className="absolute -right-10 -top-10 opacity-10">
            <Icon className="w-56 h-56 text-white" />
          </div>
          <div className="relative">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-12 h-12 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center border border-white/20">
                <Icon className="w-6 h-6 text-white" />
              </div>
              <span className="text-white/70 text-sm font-medium uppercase tracking-wide">
                {String(i + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
              </span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">{slide.title}</h2>
            <SlideBody slide={slide} />
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-between print:hidden">
          <Button variant="outline" onClick={prev} disabled={i === 0} className="gap-2" data-testid="pv-slide-prev">
            <ChevronLeft className="w-4 h-4" /> Précédent
          </Button>
          <div className="flex items-center gap-1.5">
            {SLIDES.map((s, idx) => (
              <button
                key={s.key}
                onClick={() => setI(idx)}
                className={`h-2 rounded-full transition-all ${idx === i ? 'w-6 bg-indigo-600' : 'w-2 bg-slate-300 dark:bg-slate-700 hover:bg-slate-400'}`}
                aria-label={`Slide ${idx + 1}`}
              />
            ))}
          </div>
          <Button onClick={next} disabled={i === total - 1} className="gap-2" data-testid="pv-slide-next">
            Suivant <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        </div>
      </div>
    </AppLayout>
  );
}
